import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ProjectService } from '@/services/ProjectService';
import ProjectHeader from '@/components/header';
import FileUploadCard from '@/components/FileUploadCard';
import LinkUploadCard from '@/components/LinkUploadCard';
import { useCollectionData } from '@/hooks/useCollectionData';
import CollectionNavigation from '@/components/CollectionNavigation';

const LoadingCard = () => (
    <Card className="h-[400px]">
        <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
    </Card>
);

const FileUploadScreen = ({ flowId }) => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [initialData, setInitialData] = useState({
        fileItems: [],
        linkItems: [],
    });
    const [projectData, setProjectData] = useState(null);
    const { data: collectionData, iscollectionLoading } = useCollectionData(projectData?.collectionId);

    useEffect(() => {
        if (!projectId) return;

        const loadProject = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const currentProject = await ProjectService.getProjectById(projectId);
                setProjectData(currentProject);
                setInitialData({
                    fileItems: currentProject.fileItems
                        ? typeof currentProject.fileItems === 'string'
                            ? JSON.parse(currentProject.fileItems)
                            : currentProject.fileItems
                        : [],
                    linkItems: currentProject.linkItems
                        ? typeof currentProject.linkItems === 'string'
                            ? JSON.parse(currentProject.linkItems)
                            : currentProject.linkItems
                        : [],
                });
            } catch (error) {
                console.error('Error fetching project:', error);
                setError('Failed to load project data. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        loadProject();
    }, [projectId]);

    const handleComplete = async () => {
        try {
            await ProjectService.addCompletedFlow(projectId, flowId);
            navigate(`/project/${projectId}`);
        } catch (error) {
            console.error('Error completing section:', error);
        }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-gray-100">
                <ProjectHeader showBackButton={true} />
                <main className="pt-20 px-4">
                    <div className="text-center">
                        <div className="bg-red-50 text-red-600 p-4 rounded-lg inline-block">{error}</div>
                        <Button onClick={() => window.location.reload()} className="mt-4" variant="outline">
                            Retry
                        </Button>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <ProjectHeader 
            showBackButton={true} 
            collectionData={collectionData}
            projectName={projectData?.name}
            />
            <main className="pt-20 px-4">
                {/* Render navigation only if project is part of a collection */}
                {projectData?.collectionId && (
                <CollectionNavigation 
                    // collectionId={projectData.collectionId}
                    collectionData={collectionData}
                    isLoading={iscollectionLoading}
                    currentProjectId={projectId}
                />
                )}
                {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"> */}
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 ${projectData?.collectionId ? 'md: pl-12 lg:pl-12' : ''}`}>                    
                    {isLoading ? (
                        <>
                            <LoadingCard />
                            <LoadingCard />
                        </>
                    ) : (
                        <>
                            <FileUploadCard
                                title="Floor plans, walkthroughs, inspirations"
                                projectId={projectId}
                                initialFiles={initialData.fileItems}
                            />

                            <LinkUploadCard title="Links" projectId={projectId} initialLinks={initialData.linkItems} />
                        </>
                    )}
                </div>
            </main>
            {/* Fixed footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-end items-center shadow-lg">

                <Button onClick={handleComplete} disabled={isLoading}>
                Complete This Section
                </Button>
            </div>
        </div>
    );
};

export default FileUploadScreen;
